import {
  CommandBar,
  DetailsList,
  Dropdown,
  IconButton,
  Label,
  SelectionMode,
  Stack,
  Text,
} from '@fluentui/react';
import {
  IField,
  IFieldsTab,
  IInteraction,
  ILookup,
} from '../../services/metadataService';
import { useState } from 'react';
import { QueryBuilderFluent } from '@react-querybuilder/fluent';
import QueryBuilder from 'react-querybuilder';
import { Input, useId } from '@fluentui/react-components';
import 'react-querybuilder/dist/query-builder.css';
import './styles.css';
import { useReferenceLookups } from './useReferenceLookups';

type IInteractionDisplayField = IField & {
  sourceEntityType?: string;
  sourceMetadataName?: string;
};

const TAB_EFFECT_PREFIX = 'tab:';

const formatFieldReference = (field: IInteractionDisplayField) =>
  field.sourceEntityType === 'Order'
    ? `${field.sourceMetadataName || 'Order'}::${field.name}`
    : field.name;

interface IInteractionsEditor {
  interactions: IInteraction[];
  fields: IInteractionDisplayField[];
  tabs: IFieldsTab[];
  lookups: ILookup[];
  disabled: boolean;
  onChange: (newValue: IInteraction[]) => void;
}

interface IInteractionEditor {
  fields: IInteractionDisplayField[];
  tabs: IFieldsTab[];
  lookups: ILookup[];
  interactions: IInteraction[];
  selectedRow: IInteraction | undefined;
  setSelectedRow: (value: IInteraction | undefined) => void;
  editMode: 'new' | 'edit' | undefined;
  setEditMode: (value: 'new' | 'edit' | undefined) => void;
  onChange: (newValue: IInteraction[]) => void;
}

const InteractionEditor = (props: IInteractionEditor) => {
  const {
    fields,
    tabs,
    interactions,
    selectedRow,
    setSelectedRow,
    editMode,
    setEditMode,
    onChange,
  } = props;
  const lookups = useReferenceLookups(fields, props.lookups);

  const effectTargets = [
    ...fields.map((field) => ({
      key: field.name,
      text: `${field.label} [${formatFieldReference(field)}]`,
    })),
    ...tabs.map((tab) => ({
      key: `${TAB_EFFECT_PREFIX}${tab.tabName}`,
      text: `Tab: ${tab.caption} [${tab.tabName}]`,
    })),
  ];

  const [fieldError, setFieldError] = useState<string | undefined>();

  const handleNameChange = (_ev: any, data: { value: any }) => {
    if (selectedRow !== undefined) {
      setSelectedRow({
        ...selectedRow,
        name: data.value || '',
      });
    }
  };

  const handleDescriptionChange = (_ev: any, data: { value: any }) => {
    if (selectedRow !== undefined) {
      setSelectedRow({
        ...selectedRow,
        description: data.value || '',
      });
    }
  };

  const [validFieldsForEffect, setValidFieldsForEffect] = useState<string[]>(
    effectTargets.map((target) => target.key)
  );

  const handleQueryChange = (newQuery: any) => {
    if (selectedRow !== undefined) {
      const fieldsInCondition = newQuery.rules.map((rule: any) => rule.field);
      setValidFieldsForEffect(
        effectTargets
          .filter((target) => !fieldsInCondition.includes(target.key))
          .map((target) => target.key)
      );
      setSelectedRow({
        ...selectedRow,
        condition: newQuery,
      });
    }
  };
  const nameInputId = useId('name-input');
  const descriptionInputId = useId('description-input');

  return (
    <Stack style={{ width: '100%' }} tokens={{ childrenGap: 14 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Label style={{ margin: 0, width: '15%' }} htmlFor={nameInputId}>
            Name
          </Label>
          <Input
            id={nameInputId}
            value={selectedRow ? selectedRow.name : ''}
            onChange={handleNameChange}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Label
            style={{ margin: 0, width: '15%' }}
            htmlFor={descriptionInputId}
          >
            Description
          </Label>
          <Input
            style={{ width: '85%' }}
            id={descriptionInputId}
            value={selectedRow ? selectedRow.description : ''}
            onChange={handleDescriptionChange}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <Label>Condition</Label>
          <QueryBuilderFluent>
            <QueryBuilder
              fields={fields
                .filter(
                  (field) =>
                    field.fieldType === 'Lookup' ||
                    field.fieldType === 'RadioButton' ||
                    field.fieldType === 'Checkbox' ||
                    field.fieldType === 'String' ||
                    field.fieldType === 'Number' ||
                    field.fieldType === 'NumberWithUnit' ||
                    field.fieldType === 'Date' ||
                    field.fieldType === 'AutoComplete' ||
                    field.fieldType === 'UserSelector'
                )
                .map((field) => ({
                  name: field.name,
                  label: `${field.label} [${formatFieldReference(field)}]`,
                  valueEditorType:
                    field.fieldType === 'Lookup' ||
                    field.fieldType === 'RadioButton' ||
                    field.fieldType === 'Checkbox'
                      ? 'select'
                      : undefined,
                  values:
                    field.fieldType === 'Lookup' ||
                    field.fieldType === 'RadioButton'
                      ? (
                          lookups.find(
                            (lookup) => lookup.name === field.lookupList
                          )?.values ?? []
                        ).map((item) => ({
                          name: item.key,
                          label: item.value,
                        }))
                      : field.fieldType === 'Checkbox'
                      ? [
                          { name: 'true', label: 'True' },
                          { name: 'false', label: 'False' },
                        ]
                      : [],
                }))}
              query={selectedRow ? selectedRow.condition : undefined}
              onQueryChange={handleQueryChange}
              controlClassnames={{
                queryBuilder: 'queryBuilder-branches queryBuilder-justified',
              }}
            />
          </QueryBuilderFluent>
        </div>
      </div>
      <Stack horizontal verticalAlign="center">
        <Text>Effects</Text>
        <IconButton
          iconProps={{ iconName: 'Add' }}
          onClick={() => {
            if (selectedRow !== undefined) {
              setSelectedRow({
                ...selectedRow,
                effects: [
                  ...selectedRow.effects,
                  {
                    effectType: 'Hide',
                    fields: [],
                  },
                ],
              });
            }
          }}
        >
          Add{' '}
        </IconButton>
      </Stack>
      <Stack>
        <Stack horizontal tokens={{ childrenGap: 14 }}>
          <Label styles={{ root: { width: '15%' } }}>Effects</Label>
          <Label styles={{ root: { width: '75%' } }}>
            Affected fields/tabs
          </Label>
        </Stack>
        {selectedRow?.effects?.map((effect, index) => (
          <Stack horizontal tokens={{ childrenGap: 14 }}>
            <Dropdown
              id={`effect-type-${index}`}
              styles={{ root: { width: '15%' } }}
              options={[
                {
                  key: 'Show',
                  text: 'Show',
                },
                {
                  key: 'Hide',
                  text: 'Hide',
                },
              ]}
              selectedKey={effect.effectType}
              onChange={(_event, item) => {
                if (selectedRow !== undefined) {
                  setSelectedRow({
                    ...selectedRow,
                    effects: selectedRow.effects.map((e) =>
                      e === effect
                        ? { ...e, effectType: item?.key.toString() ?? 'Show' }
                        : e
                    ),
                  });
                }
              }}
            />
            <Dropdown
              id={`effect-fields-${index}`}
              styles={{ root: { width: '75%' } }}
              // selectedKey={selectedItem ? selectedItem.key : undefined}
              // eslint-disable-next-line react/jsx-no-bind
              placeholder="Select an option"
              options={effectTargets.filter((target) =>
                validFieldsForEffect.includes(target.key)
              )}
              multiSelect
              selectedKeys={effect.fields}
              onChange={(_event, item) => {
                if (item) {
                  const selectedKeys = item.selected
                    ? [...effect.fields, item.key as string]
                    : effect.fields.filter((key) => key !== item.key);
                  if (selectedRow !== undefined) {
                    setSelectedRow({
                      ...selectedRow,
                      effects: selectedRow.effects.map((e) =>
                        e === effect ? { ...e, fields: selectedKeys } : e
                      ),
                    });
                  }
                }
              }}
            />
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              onClick={() => {
                if (selectedRow !== undefined) {
                  setSelectedRow({
                    ...selectedRow,
                    effects: selectedRow.effects.filter((e) => e !== effect),
                  });
                }
              }}
            />
          </Stack>
        ))}
      </Stack>
      <Stack horizontal horizontalAlign="end">
        <Text variant="medium" style={{ color: 'red' }}>
          {fieldError}
        </Text>
        <IconButton
          iconProps={{ iconName: 'CheckMark' }}
          onClick={() => {
            if (!selectedRow) return;

            if (editMode === 'new') {
              const newInteractions: IInteraction[] = [
                ...interactions,
                {
                  name: selectedRow.name,
                  description: selectedRow.description,
                  condition: selectedRow.condition,
                  effects: selectedRow.effects,
                },
              ];
              onChange(newInteractions);
            } else if (editMode === 'edit') {
              // Use originalName to find the correct index
              const index = interactions.findIndex(
                (i) => i.name === (selectedRow as any).originalName
              );
              if (index !== -1) {
                const updatedInteractions: IInteraction[] = [
                  ...interactions.slice(0, index),
                  {
                    name: selectedRow.name,
                    description: selectedRow.description,
                    condition: selectedRow.condition,
                    effects: selectedRow.effects,
                  },
                  ...interactions.slice(index + 1),
                ];
                onChange(updatedInteractions);
              }
            }
            setEditMode(undefined);
            setSelectedRow(undefined);
          }}
        />
        <IconButton
          iconProps={{ iconName: 'Cancel' }}
          onClick={() => {
            setEditMode(undefined);
            setSelectedRow(undefined);
          }}
        />
      </Stack>
    </Stack>
  );
};

const InteractionsEditor = (props: IInteractionsEditor) => {
  const [editMode, setEditMode] = useState<'new' | 'edit' | undefined>(
    undefined
  );
  const [selectedRow, setSelectedRow] = useState<IInteraction | undefined>(
    undefined
  );
  const interactions = props.interactions;

  return (
    <Stack style={{ width: 525 }}>
      <CommandBar
        items={[
          {
            key: 'AddInteraction',
            text: 'Add',
            disabled: props.disabled,
            iconProps: { iconName: 'Add' },
            onClick: () => {
              setEditMode('new');
              setSelectedRow({
                name: '',
                description: '',
                condition: {
                  combinator: 'And',
                  not: false,
                  rules: [],
                },
                effects: [
                  {
                    effectType: 'Hide',
                    fields: [],
                  },
                ],
              });
            },
          },
        ]}
      />
      {editMode === undefined ? (
        <DetailsList
          columns={[
            {
              key: 'name',
              name: 'Name',
              fieldName: 'name',
              minWidth: 150,
              maxWidth: 250,
              isMultiline: false,
              isResizable: true,
            },
            {
              key: 'description',
              name: 'Description',
              fieldName: 'description',
              minWidth: 200,
              maxWidth: 300,
              isMultiline: false,
              isResizable: true,
            },
            {
              key: 'action',
              name: 'Action',
              minWidth: 85,
              onRender: (item: any, index?: number) => (
                <Stack horizontal>
                  <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    onClick={() => {
                      setEditMode('edit');
                      // Store originalName for editing
                      setSelectedRow({ ...item, originalName: item.name });
                    }}
                  />
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => {
                      if (index !== undefined) {
                        const updatedInteractions = interactions.filter(
                          (_, i) => i !== index
                        );
                        props.onChange(updatedInteractions);
                      }
                    }}
                  />
                </Stack>
              ),
            },
          ]}
          items={props.interactions.map((interaction) => ({
            key: interaction.name,
            name: interaction.name,
            description: interaction.description,
            condition: interaction.condition,
            effects: interaction.effects,
          }))}
          selectionMode={SelectionMode.none}
        />
      ) : (
        <InteractionEditor
          fields={props.fields}
          tabs={props.tabs}
          lookups={props.lookups}
          interactions={interactions}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          editMode={editMode}
          setEditMode={setEditMode}
          onChange={props.onChange}
        />
      )}
    </Stack>
  );
};

export default InteractionsEditor;
